import classNames from 'classnames';
import { formatCountryCurrency } from 'eco-design-micro/utils/currency';
import getLocal from 'eco-design-reliance/getLocal';
import EcoCountDown from 'eco-design/elements/eco-count-down';
import React from 'react';
import EcoImg from 'eco-design/base/eco-img';
import EcoButton from 'eco-design/base/eco-button';
import { i18n } from './i18n';
import './index.scss';
import { EcoBundleEarlyBirdItemProps, EcoBundleEarlyBirdProps } from './type';
import AmazonIcon from '../amazon';
/**
 *  @param name EcoBundleEarlyBird 早鸟/众筹 组件
 *  @param description Wave 3 / Glacier 2 超早鸟/早鸟/常规阶段 使用中
 *  @param author kalkin
 *  @param date 2025-03-12
 *  @param version v1.0.0
 *  @param modify 2025-04-04 kalkin 增补早鸟变化类型 => 'countdown' | 'inventory' 倒计时类型：倒计时结束进入下一阶段 ｜ 库存不足进入下一阶段
 *  @param version v1.0.1
 */

const EcoBundleEarlyBird: React.FC<EcoBundleEarlyBirdProps> = ({ className, theme = 'white', items, onCountdownEnd }) => {
    const prefixCls = `eco-bundle-early-bird`;
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-is-single`]: items.length === 1,
    });
    const { locale = 'us', query } = getLocal();
    const lang = i18n[locale as 'us'] || i18n['us'];

    const handleCountdownEnd = (item: EcoBundleEarlyBirdItemProps) => {
        onCountdownEnd && onCountdownEnd(item);
    };

    return (
        <div className={classes}>
            <ul className={`${prefixCls}-items`}>
                {items?.map((item: EcoBundleEarlyBirdItemProps, k: number) => {
                    const {
                        tag,
                        title,
                        desc,
                        pc,
                        delivery,
                        price,
                        compareAtPrice,
                        variantId,
                        inventoryQuantity,
                        totalInventoryQuantity,
                        type,
                        countdown,
                        isOnSale = true,
                        amazonUrl,
                    } = item;
                    const hasTag = items?.some((item: EcoBundleEarlyBirdItemProps) => item?.tag);
                    return (
                        <li className={`${prefixCls}-items-item`} key={k}>
                            <div className={`${prefixCls}-items-item-top`}>
                                <div className={`${prefixCls}-items-item-top-header${hasTag ? '' : ` no-tag`}`}>
                                    {hasTag && (
                                        <div
                                            className={`${prefixCls}-items-item-top-header-tag${tag === 'SUPER EARLY BIRD' ? ' tag-super' : ''} tag-${
                                                tag ? 'show' : 'hidden'
                                            }`}
                                        >
                                            {tag === 'SUPER EARLY BIRD' ? (
                                                <>
                                                    <span className={`${prefixCls}-items-item-top-header-tag-filled`}>{lang.super}</span>
                                                    <span className={`${prefixCls}-items-item-top-header-tag-outlined`}>{lang.early_bird}</span>
                                                </>
                                            ) : (
                                                tag || ''
                                            )}
                                        </div>
                                    )}
                                    {title && <h2 className={`${prefixCls}-items-item-top-header-title`}>{title}</h2>}
                                    {pc && <EcoImg className={`${prefixCls}-items-item-top-header-img`} pc={pc} />}
                                </div>
                                <div className={`${prefixCls}-items-item-top-info`}>
                                    <div className={`${prefixCls}-items-item-top-info-left${delivery ? '' : ` no-delivery`}`}>
                                        <div className={`${prefixCls}-items-item-top-info-left-include`}>{lang.include}</div>
                                        <ul className={`${prefixCls}-items-item-top-info-left-descs`}>
                                            {desc?.map((list: string, m: number) => (
                                                <li className={`${prefixCls}-items-item-top-info-left-descs-desc`} key={`${k}_${m}`}>
                                                    {list}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {delivery && (
                                        <div className={`${prefixCls}-items-item-top-info-right`}>
                                            <p className={`${prefixCls}-items-item-top-info-right-delivery`}>{lang.delivery}</p>
                                            <p className={`${prefixCls}-items-item-top-info-right-date`}>{delivery}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={`${prefixCls}-items-item-bottom`}>
                                {type === 'inventory' && (
                                    <>
                                        <div className={`${prefixCls}-items-item-bottom-inventory`}>
                                            <span className={`${prefixCls}-items-item-bottom-inventory-backer`}>
                                                {lang.backers.replace('{BACKERS}', `${Number(totalInventoryQuantity) - Number(inventoryQuantity)}`)}
                                            </span>
                                            <span className={`${prefixCls}-items-item-bottom-inventory-left${Number(inventoryQuantity) <= 10 ? ' red' : ''}`}>
                                                {lang.left.replace('{left}', `${inventoryQuantity}`)}
                                            </span>
                                        </div>
                                        <div className={`${prefixCls}-items-item-bottom-bar`}>
                                            <div
                                                className={`${prefixCls}-items-item-bottom-bar-active`}
                                                style={{
                                                    width: `${
                                                        ((Number(totalInventoryQuantity) - Number(inventoryQuantity)) / Number(totalInventoryQuantity)) * 100
                                                    }%`,
                                                }}
                                            ></div>
                                        </div>
                                    </>
                                )}
                                {type === 'countdown' && countdown && (
                                    <div className={`${prefixCls}-items-item-bottom-countdown`}>
                                        <span className={`${prefixCls}-items-item-bottom-countdown-text`}>{lang.countdown}</span>
                                        <div className={`${prefixCls}-items-item-bottom-countdown-box`}>
                                            <EcoCountDown
                                                endTime={
                                                    query?._timer
                                                        ? new Date().getTime() +
                                                          new Date(`${countdown} GMT+8`).getTime() -
                                                          new Date(`${query?._timer} GMT+8`).getTime()
                                                        : new Date(`${countdown} GMT+8`).getTime()
                                                }
                                                onCountdownEnd={() => handleCountdownEnd(item)}
                                            />
                                            <div className={`${prefixCls}-items-item-bottom-countdown-box-units`}>
                                                <div className={`${prefixCls}-items-item-bottom-countdown-box-units-unit`}>
                                                    <span>{lang.countdown_d}</span>
                                                </div>
                                                <div className={`${prefixCls}-items-item-bottom-countdown-box-units-unit`}>
                                                    <span>{lang.countdown_h}</span>
                                                </div>
                                                <div className={`${prefixCls}-items-item-bottom-countdown-box-units-unit`}>
                                                    <span>{lang.countdown_m}</span>
                                                </div>
                                                <div className={`${prefixCls}-items-item-bottom-countdown-box-units-unit`}>
                                                    <span>{lang.countdown_s}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {isOnSale ? (
                                    <>
                                        <div className={`${prefixCls}-items-item-bottom-action`}>
                                            {price !== -1 && compareAtPrice !== -1 && (
                                                <div className={`${prefixCls}-items-item-bottom-action-price`}>
                                                    <div className={`${prefixCls}-items-item-bottom-action-price-number`}>
                                                        <span className={`${prefixCls}-items-item-bottom-action-price-number-currency`}>
                                                            {formatCountryCurrency(Math.round(Number(price) * 100) / 100, locale)}
                                                        </span>
                                                        {compareAtPrice !== price && (
                                                            <span className={`${prefixCls}-items-item-bottom-action-price-number-compare`}>
                                                                {formatCountryCurrency(Math.round(Number(compareAtPrice) * 100) / 100, locale)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {compareAtPrice !== price && (
                                                        <div className={`${prefixCls}-items-item-bottom-action-price-save`}>
                                                            {lang.save}{' '}
                                                            {formatCountryCurrency(Math.round((Number(compareAtPrice) - Number(price)) * 100) / 100, locale)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`${prefixCls}-items-item-bottom-action-btn-group`}>
                                            {amazonUrl && (
                                                <EcoButton
                                                    className={`${prefixCls}-items-item-bottom-action-btn`}
                                                    lHref={amazonUrl}
                                                    lChildren={lang.buy_on_amazon}
                                                    lType="filled"
                                                    lTheme="black"
                                                    lIconL={<AmazonIcon />}
                                                />
                                            )}
                                            <EcoButton
                                                className={`${prefixCls}-items-item-bottom-action-btn ${amazonUrl ? '' : 'no-amazon'}`}
                                                lHref={`https://${locale}.ecoflow.com/cart/${variantId}:1?channel=buy_button`}
                                                lChildren={Number(inventoryQuantity) <= 0 ? lang.sold_out : lang.buy_now}
                                                lType="outlined"
                                                lTheme="black"
                                                lDisabled={Number(inventoryQuantity) <= 0}
                                                lDataLayer={{
                                                    event: 'uaEvent',
                                                    event_name: 'checkout',
                                                    button_name: `${Number(inventoryQuantity) <= 0 ? lang.sold_out : lang.buy_now}_${title}`,
                                                }}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div className={`${prefixCls}-items-item-bottom-action coming_soon`}>
                                        <EcoButton
                                            className={`${prefixCls}-items-item-bottom-action-btn`}
                                            lChildren={lang.coming_soon}
                                            lType="filled"
                                            lTheme="blue"
                                            lDisabled
                                        />
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default EcoBundleEarlyBird;
